import { ApolloQueryResult } from '@apollo/client';
import { isEmpty } from 'lodash';
import * as ms from 'milliseconds';
import { Queries } from '@shared/api';
import { getSharedApolloClient } from '@shared/api/client';
import { API } from '@shared/types/api';
import { i18n } from '@shared/util/i18n';
import { getController, setControlledTimeout } from '@shared/util/timeout';
import { getBaseUrlFromUrl } from './util';

export function fetchLinkData(url: string, timeout = ms.seconds(5)) {
  return async (dispatch: MBDispatch, getState: MBGetState) => {
    const existingLinkData = getState().linkData[url];
    if (existingLinkData) {
      return;
    }

    const linkData = await linkRequester.request(url, timeout);
    const defaultLinkData: LinkData = {
      url,
      title: url,
      description: i18n('chat.linkPreview.defaultDescription'),
    };
    dispatch({
      type: 'LINK:UPDATE',
      value: { url, linkData: linkData ?? defaultLinkData },
    });
  };
}

class LinkDataRequester {
  private fetchRequests: Record<string, Promise<LinkData | null>> = {};

  /*
        Store promises to fetch url data so we don't perform the same
        request more than once per link.
    */
  request(url: string, timeout: number): Promise<LinkData | null> {
    if (!!this.fetchRequests[url]) {
      return this.fetchRequests[url];
    }
    const promise = this.fetchFromAPI(url, timeout);
    promise.catch(() => delete this.fetchRequests[url]);
    this.fetchRequests[url] = promise;
    return promise;
  }

  /*
        TODO(fant): all of this logic should ideally be supplied by the backend.
        It's pretty bs that the client has to manually request all link details
        in separate requests...
    */
  private async fetchFromAPI(url: string, timeout: number): Promise<LinkData | null> {
    const { signal, abort } = getController();
    setControlledTimeout(abort, timeout, signal);
    const apolloClient = getSharedApolloClient();
    let res: ApolloQueryResult<API.FetchLinkDetailsQuery>;
    try {
      // Apollo rejects (with AbortError) when the timeout fires — catch returns null
      // so the caller falls back to default link data instead of throwing.
      res = await apolloClient
        .query<API.FetchLinkDetailsQuery, API.FetchLinkDetailsQueryVariables>({
          query: Queries.link.details,
          variables: { url },
          // Note(fant): need to specify fetch policy when querying
          // using apollo client directly (network vs cache)
          fetchPolicy: 'network-only',
          context: { fetchOptions: { signal } },
        })
        .finally(abort);
    } catch {
      return null;
    }

    // res.data.details is {} (empty object) for invalid urls
    // If we get an url that fails, we try to request details
    // from the page's main url (ex github.com instead of
    // github.com/townsquared). In this way, we can create
    // some previews even though we can't get it for the
    // specific url.
    if (!res?.data?.details || isEmpty(res?.data?.details)) {
      const baseUrl = getBaseUrlFromUrl(url);
      if (!baseUrl || url === baseUrl) {
        return null;
      }
      const baseUrlData = await this.fetchFromAPI(baseUrl, timeout);
      if (!baseUrlData) {
        return null;
      }

      return { ...baseUrlData, url };
    }

    return {
      // We return the url from the API, which will eg have prefixed
      // "https://" if it's missing (eg for lol.com => https://lol.com)
      // If this returns null (e.g. for Google Drive urls - 503?),
      // we return the provided url. The provided url will fail if
      url: url.indexOf('http') !== 0 ? `https://${url}` : url,
      title: res.data.details.title,
      description: res.data.details.description,
      imageUrl: res.data.details.thumbnail_url,
      imageWidth: res.data.details.thumbnail_width,
      imageHeight: res.data.details.thumbnail_height,
    };
  }
}

export const linkRequester = new LinkDataRequester();
